import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { COMMON_TRANSLATION } from '@constants/index';
import { ConfirmModal, NotificationVariants } from '@base-app';
import { FETCH_DELAY } from '@modules/shared/constants';
import { resetMFA, resetUserPassword, updateUser } from '@modules/teams/services';
import { TeamListProps } from './TeamList.types';
import { TEAMS_QUERY_KEY, TEAMS_TRANSLATION } from '@modules/teams/constants';
import { useAddNotification } from '@modules/notifications/store';
import { useModal } from '@components/hooks';
import { useDeleteUserModal, useMutationDeactivateUser, useMutationReactivateUser } from '@modules/teams/hooks';
import { UserToUpdate } from '@modules/teams/types/UpdateTeamMember';

import DeactivateMemberModal from '../DeactivateMemberModal/DeactivateMemberModal';
import DeleteUserModal from '../DeleteUserModal/DeleteUserModal';
import ReactivateMemberModal from '../ReactivateMemberModal/ReactivateMemberModal';
import ResetMFAModal from '../ResetMFAModal/ResetMFAModal';
import TeamsListDesktop from '../TeamsListDesktop/TeamsListDesktop';
import TeamsListMobile from '../TeamsListMobile/TeamsListMobile';
import UpdateTeamMemberModal from '../UpdateTeamMemberModal/UpdateTeamMemberModal';

const TeamsList: FC<TeamListProps> = ({ currentUser, data }) => {
  const { t } = useTranslation(['common', 'teams']);
  const addNotification = useAddNotification();
  const queryClient = useQueryClient();

  const [userToDeactivate, setUserToDeactivate] = useState<string | undefined>();
  const [userToReactivate, setUserToReactivate] = useState<string | undefined>();
  const [userToResetMFA, setUserToResetMFA] = useState<string | undefined>();
  const [userToResetPassword, setUserToResetPassword] = useState<string | undefined>();
  const [userToUpdate, setUserToUpdate] = useState<UserToUpdate | undefined>();
  const [updatedUser, setUpdatedUser] = useState<UserToUpdate | undefined>();
  const editUserFormValues = useRef<UserToUpdate | undefined>();

  /**
   * This useModal is to control the modal that appears when a user is editing a
   * team member and tries to close the UpdateTeamMemberModal with unsaved changes.
   * */
  const { isShowing: isShowingCloseModalConfirm, toggle: toggleCloseModalConfirm } = useModal();

  const {
    userToDelete,
    onCancel: handleDeleteCancel,
    onConfirm: handleDeleteConfirm,
    deleteUser,
  } = useDeleteUserModal();

  const { mutate: deactivateUserMutate } = useMutationDeactivateUser(
    handleMutationDeactivateUserSuccess,
    handleMutationDeactivateUserError,
  );

  const { mutate: reactivateUserMutate } = useMutationReactivateUser(
    handleMutationReactivateUserSuccess,
    handleMutationReactivateUserError,
  );

  const handleDeactivate = (userId: string): void => {
    setUserToDeactivate(userId);
  };

  const handleDeactivateConfirm = (userId: string): void => {
    deactivateUserMutate(userId);
    setUserToDeactivate(undefined);
  };

  const handleDeactivateCancel = (): void => {
    setUserToDeactivate(undefined);
  };

  function handleMutationDeactivateUserSuccess(): void {
    addNotification({
      title: t('deactivate_success', {
        ns: 'teams',
      }),
      type: NotificationVariants.SUCCESS,
    });
  }

  function handleMutationDeactivateUserError(): void {
    addNotification({
      title: t('deactivate_error', {
        ns: 'teams',
      }),
      type: NotificationVariants.ERROR,
    });
  }

  const handleDelete = (userId: string): void => {
    deleteUser(userId);
  };

  const handlePasswordResetConfirm = async (userId: string): Promise<any> => {
    await resetUserPassword(userId)
      .then(() => {
        addNotification({
          title: t('password_reset_email_sent', {
            ns: 'teams',
          }),
          type: NotificationVariants.SUCCESS,
          autoClose: true,
        });
      })
      .catch((error) => {
        // TODO: Error notification message TBD
        throw new Error(error);
      });
  };

  const handleReactivate = (userId: string): void => {
    setUserToReactivate(userId);
  };

  const handleReactivateConfirm = (userId: string): void => {
    reactivateUserMutate(userId);
    setUserToReactivate(undefined);
  };

  const handleReactivateCancel = (): void => {
    setUserToReactivate(undefined);
  };

  const handleMFAReset = (userId: string): void => {
    setUserToResetMFA(userId);
  };

  const handleMFAResetCancel = (): void => {
    setUserToResetMFA(undefined);
  };

  const handlePasswordReset = (userId: string): void => {
    setUserToResetPassword(userId);
  };

  const handlePasswordResetCancel = (): void => {
    setUserToResetPassword(undefined);
  };

  const handleMFAResetConfirm = async (userId: string): Promise<any> => {
    await resetMFA(userId)
      .then(() => {
        addNotification({
          title: t('reset_mfa_success', {
            ns: 'teams',
          }),
          type: NotificationVariants.SUCCESS,
          autoClose: true,
        });
      })
      .catch(() => {
        addNotification({
          title: t('reset_mfa_error', {
            ns: 'teams',
          }),
          type: NotificationVariants.ERROR,
        });
      });

    setUserToResetMFA(undefined);
  };

  const handleUserUpdate = (user: any): void => {
    const newUser = { ...user, role: user.pp_role };
    setUserToUpdate({ ...newUser });
    setUpdatedUser({ ...newUser });
    handleUserUpdateChange({ ...newUser });
  };

  const handleUserUpdateChange = (values: UserToUpdate): void => {
    editUserFormValues.current = values;
  };

  const handleUpdateUserClose = (): void => {
    if (
      editUserFormValues.current?.firstName !== userToUpdate?.firstName ||
      editUserFormValues.current?.lastName !== userToUpdate?.lastName ||
      editUserFormValues.current?.email !== userToUpdate?.email
    ) {
      toggleCloseModalConfirm();
    } else {
      setUserToUpdate(undefined);
    }
  };

  const handleCloseConfirmationModalConfirm = (): void => {
    toggleCloseModalConfirm();
    setUserToUpdate(undefined);
  };

  const handleUserUpdateConfirm = (user: UserToUpdate): void => {
    if (userToUpdate?.id !== undefined) {
      updateUser(userToUpdate.id, user)
        .then(() => {
          setTimeout(() => {
            void queryClient.invalidateQueries([TEAMS_QUERY_KEY]);

            addNotification({
              title: t('update_team_member_success', {
                ns: 'teams',
              }),
              type: NotificationVariants.SUCCESS,
              autoClose: true,
            });
          }, FETCH_DELAY);
        })
        .catch(() => {
          addNotification({
            title: t('update_team_member_error', {
              ns: 'teams',
            }),
            type: NotificationVariants.ERROR,
          });
        });
    }

    setUserToUpdate(undefined);
  };

  function handleMutationReactivateUserSuccess(): void {
    addNotification({
      title: t('reactivate_success', {
        ns: 'teams',
      }),
      type: NotificationVariants.SUCCESS,
    });
  }

  function handleMutationReactivateUserError(): void {
    addNotification({
      title: t('reactivate_error', {
        ns: 'teams',
      }),
      type: NotificationVariants.ERROR,
    });
  }

  return (
    <>
      <TeamsListMobile
        currentUser={currentUser}
        data={data}
        onDeactivate={handleDeactivate}
        onDelete={handleDelete}
        onMFAReset={handleMFAReset}
        onPasswordReset={handlePasswordReset}
        onReactivate={handleReactivate}
        onUpdate={handleUserUpdate}
      />
      <TeamsListDesktop
        currentUser={currentUser}
        data={data}
        onDeactivate={handleDeactivate}
        onDelete={handleDelete}
        onMFAReset={handleMFAReset}
        onReactivate={handleReactivate}
        onPasswordReset={handlePasswordReset}
        onUpdate={handleUserUpdate}
      />

      {userToDeactivate !== undefined && (
        <DeactivateMemberModal
          onCancel={handleDeactivateCancel}
          onConfirm={handleDeactivateConfirm}
          show={userToDeactivate !== undefined}
          selectedUserId={userToDeactivate}
        />
      )}
      {userToReactivate !== undefined && (
        <ReactivateMemberModal
          onCancel={handleReactivateCancel}
          onConfirm={handleReactivateConfirm}
          show={userToReactivate !== undefined}
          selectedUserId={userToReactivate}
        />
      )}
      {userToDelete !== undefined && (
        <DeleteUserModal
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          show={userToDelete !== undefined}
          selectedUserId={userToDelete}
        />
      )}
      {userToResetMFA !== undefined && (
        <ResetMFAModal
          onCancel={handleMFAResetCancel}
          onConfirm={handleMFAResetConfirm}
          show={userToResetMFA !== undefined}
          selectedUserId={userToResetMFA}
        />
      )}
      {userToResetPassword !== undefined && (
        <ConfirmModal
          confirmAction={{
            onClick: handlePasswordResetConfirm,
            text: t('reset_password_modal.confirm', { ns: TEAMS_TRANSLATION }),
          }}
          cancelAction={{
            onClick: handlePasswordResetCancel,
            text: t('modal_cancel', { ns: COMMON_TRANSLATION }),
          }}
          title={t('reset_password_modal.title', { ns: TEAMS_TRANSLATION })}
          show={userToResetPassword !== undefined}
          data={userToResetPassword}
        />
      )}
      {userToUpdate !== undefined && updatedUser !== undefined && (
        <UpdateTeamMemberModal
          onChange={handleUserUpdateChange}
          onClose={handleUpdateUserClose}
          onUpdate={handleUserUpdateConfirm}
          show={userToUpdate !== undefined}
          user={userToUpdate}
        />
      )}

      {isShowingCloseModalConfirm && (
        <ConfirmModal
          confirmAction={{
            onClick: handleCloseConfirmationModalConfirm,
            text: t('update_team_member_modal_close.confirm', { ns: TEAMS_TRANSLATION }),
          }}
          cancelAction={{
            onClick: toggleCloseModalConfirm,
            text: t('modal_cancel', { ns: COMMON_TRANSLATION }),
          }}
          description={t('update_team_member_modal_close.description', { ns: TEAMS_TRANSLATION })}
          title={t('update_team_member_modal_close.title', { ns: TEAMS_TRANSLATION })}
          show={isShowingCloseModalConfirm}
          data={updatedUser}
        />
      )}
    </>
  );
};

export default TeamsList;
